import { io } from 'socket.io-client';
import { getActiveSubscription } from '@/data/session/session-globals';

export const DOCUMENT_ID_PREFIXES = {
    NOTIFICATIONS: 'notifications',
    DASHBOARD: 'DASHBOARD',
    REPLAY: 'REPLAY',
    IDEAS: 'IDEAS',
    FEEDBACK: 'FEEDBACK',
};

export const WS_EVENT_TYPES = {
    THREADS_BY_DOCUMENT_ID: 'threadsByDocumentId',
    NEW_THREAD: 'newThread',
    NEW_COMMENT: 'newComment',
    NEW_EMOJI_REACTION: 'newEmojiReaction',
};

// Holds a map of documentIds and each callback registered to the documentId
const documentIdCallbacks = new Map();
let socket;

const isLocal = __PROCESS__.LOCAL_ENV;
const socketHostname = isLocal ? 'local.pendo.io:8888' : window.location.hostname;

// connect to server
export function connectToWebSocketServer ({ dispatch }) {
    if (socket) return;

    const serverUrl = `wss://${socketHostname}/in-app-commenting`;
    const opts = {
        path: '/ws2/socket.io',
        transports: ['websocket'],
        forceNew: false,
        autoConnect: false,
    };
    socket = io(serverUrl, opts);
    socket.connect();

    socket.on('connect', () => {
        dispatch('pendoCollaboration/init', null, { root: true });

        // Rejoin rooms on reconnection
        if (documentIdCallbacks.size > 0) {
            const subId = getActiveSubscription()?.id;
            for (const documentId of documentIdCallbacks.keys()) {
                socket.emit('join', { documentId, subId });
            }
        }
    });

    socket.on('connect_error', () => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
    });

    socket.on('disconnect', (reason) => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
        // socket.io won't auto-reconnect for these reasons
        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
            socket = null;
            documentIdCallbacks.clear();
        }
        // For transport errors/timeouts, socket.io reconnects automatically
        // and the 'connect' handler will rejoin rooms
    });

    socket.on('message', ({ type, documentId, data }) => {
        documentIdCallbacks.get(documentId)?.forEach((cb) => cb({ type, data }));
    });
}

export function subscribeToDocument ({ documentId, callback }) {
    if (!socket) return () => {};

    // Join the room if not already subscribed
    if (!documentIdCallbacks.has(documentId)) {
        documentIdCallbacks.set(documentId, new Set());
        const subId = getActiveSubscription()?.id;
        socket.emit('join', { documentId, subId });
    }

    documentIdCallbacks.get(documentId).add(callback);

    // Return unsubscribe function
    return function unsubscribe () {
        const callbacks = documentIdCallbacks.get(documentId);
        if (!callbacks) return;
        callbacks.delete(callback);
        if (callbacks.size === 0) {
            documentIdCallbacks.delete(documentId);
            socket?.emit('leave', documentId);
        }
    };
}

export function createThread ({ documentId, targetElementId, textBody }) {
    const subId = getActiveSubscription()?.id;
    socket?.emit('createThread', { subId, documentId, targetElementId, textBody });
}

export function addComment ({ documentId, textBody, threadId }) {
    const subId = getActiveSubscription()?.id;
    socket?.emit('addComment', { subId, documentId, textBody, threadId });
}

export function addEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    socket?.emit('emojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}
